import * as React from 'react';
import {CSSProperties, useEffect, useRef} from 'react';
import {faAngleDown, faEllipsisV, faPlus} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

import {Tooltip} from '../Tooltip/Tooltip';

interface ContextMenuItem {
    disabled?: boolean;
    divider?: boolean;
    hidden?: boolean;
    href?: string;
    name?: string;
    onClick?: (event?) => void;
    tooltip?: string;
    e2e?: string;
}

export type ContextPosition = `center` | `static` | `default` | `top`;

export interface ContextMenuProps {
    ariaLabel?: string;
    className?: string;
    dark?: boolean;
    darkSquare?: boolean;
    disabled?: boolean;
    e2e?: string;
    index: number | string;
    items: ContextMenuItem[];
    menuClasses?: string;
    options?: {dark?: boolean; downIcon?: boolean; plusIcon?: boolean; alignRight?: boolean};
    position?: ContextPosition;
    rounded?: boolean;
    style?: CSSProperties;
}

export const ContextMenu = ({
    ariaLabel,
    className,
    darkSquare = false,
    disabled,
    e2e,
    index,
    items,
    menuClasses,
    options = {dark: false, downIcon: false, plusIcon: false, alignRight: true},
    position,
    rounded = true,
    style,
}: ContextMenuProps) => {
    const [showMenu, setShowMenu] = React.useState(false);
    const dropdownRef = useRef(null);

    // Ensure only one ContextMenu is open at a time
    useEffect(() => {
        const closeDropdown = (e) => {
            if (!dropdownRef.current?.contains(e.target)) setShowMenu(false);
        };
        document.body.addEventListener('click', closeDropdown);
        return () => document.body.removeEventListener('click', closeDropdown);
    }, []);

    /**
     * Template
     */
    return (
        <>
            {items.find(({hidden}) => !hidden) && (
                <div>
                    <div
                        className={`tw-cursor-pointer ${
                            rounded ? '' : 'tw-h-[38px] tw-w-[38px] tw-items-center tw-text-center tw-rounded tw-border'
                        } ${
                            position === 'default'
                                ? 'tw-ml-4 tw-relative [&_button]:!tw-relative [&_button]:!tw-top-0'
                                : position === 'top'
                                ? 'tw-absolute tw-right-[11px] tw-top-[34px]'
                                : position === 'center'
                                ? 'tw-absolute tw-top-1/2 tw-right-[26px]'
                                : position === 'static'
                                ? 'tw-relative tw-right-2.5 tw-top-[17px]'
                                : 'tw-ml-4 tw-relative [&_button]:!tw-relative [&_button]:!tw-top-0'
                        }${disabled ? ' [&_button]:tw-bg-gray-100 !tw-cursor-not-allowed !tw-pointer-events-none' : ''} ${
                            className ? ' ' + className : ''
                        }`}
                        data-e2e={e2e}
                        style={style}
                    >
                        <button
                            aria-label={ariaLabel}
                            className={`!tw-z-auto tw-whitespace-nowrap tw-text-center tw-align-middle tw-select-none tw-py-1.5 tw-px-3 tw-text-base tw-leading-normal tw-cursor-pointer tw-text-gray-700 ${
                                darkSquare
                                    ? '!tw-bg-gray-600 hover:!tw-bg-gray-700 !tw-text-white tw-w-[38px] tw-text-center tw-items-center '
                                    : 'tw-text-gray-650'
                            } -tw-mr-2 tw-no-underline tw-flex tw-items-center tw-justify-center ${
                                options?.dark && 'tw-bg-gray-650 tw-text-white'
                            } ${
                                rounded
                                    ? 'tw-rounded-[50%] tw-bg-white tw-border tw-h-[38px] tw-w-[38px] tw-absolute tw-z-[40] tw-right-[-3px] tw-top-[-18px]'
                                    : 'tw-rounded-sm'
                            }`}
                            id={`dropdown-basic_${index}`}
                            onClick={() => {
                                setShowMenu(!showMenu);
                            }}
                            ref={dropdownRef}
                            type="button"
                        >
                            {options.downIcon ? (
                                <FontAwesomeIcon
                                    icon={faAngleDown}
                                    size="xl"
                                    style={{marginTop: 2}}
                                    suppressHydrationWarning
                                />
                            ) : options.plusIcon ? (
                                <FontAwesomeIcon
                                    className={`tw-text-white !tw-bg-gray-650 hover:!tw-bg-gray-700`}
                                    icon={faPlus}
                                    size="sm"
                                    suppressHydrationWarning
                                />
                            ) : (
                                <FontAwesomeIcon
                                    className={`${
                                        disabled ? 'tw-text-gray-400 hover:tw-text-gray-400' : 'tw-text-gray-650 hover:tw-text-gray-650'
                                    } ${
                                        options.dark ||
                                        (darkSquare &&
                                            `${
                                                disabled
                                                    ? '!tw-text-gray-300 !hover:!tw-text-gray-300'
                                                    : '!tw-text-white hover:!tw-text-white'
                                            }`)
                                    }`}
                                    icon={faEllipsisV}
                                    size="xl"
                                    suppressHydrationWarning
                                />
                            )}
                        </button>
                        <div
                            className={`tw-absolute tw-cursor-auto tw-inset-[0px_0px_auto_auto] tw-m-0 tw-translate-x-[11px] tw-translate-y-[40px] tw-z-[2001] tw-float-left tw-min-w-[10rem] tw-px-0 tw-text-base tw-text-left tw-list-none tw-bg-white tw-border tw-rounded-sm ${
                                showMenu ? 'tw-block' : '!tw-hidden'
                            } ${menuClasses}`}
                            onClick={() => {
                                setShowMenu(false);
                            }}
                        >
                            {items
                                .filter((item) => !item.hidden)
                                .map((item, key) =>
                                    item.divider ? (
                                        <div
                                            className="tw-h-0 tw-my-2 tw-mx-0 tw-overflow-hidden tw-border-t"
                                            key={key}
                                        />
                                    ) : item.tooltip ? (
                                        <Tooltip
                                            id={key.toString()}
                                            key={key}
                                            title={item.tooltip}
                                        >
                                            <a
                                                className={`hover:tw-text-gray-650 tw-block tw-no-underline tw-w-full tw-py-3 tw-px-4 tw-clear-both tw-font-normal tw-text-inherit tw-whitespace-nowrap tw-bg-transparent hover:tw-no-underline ${
                                                    item.disabled
                                                        ? '!tw-text-gray-300 tw-cursor-not-allowed !tw-pointer-events-none'
                                                        : 'tw-cursor-pointer hover:tw-bg-gray-200 !tw-text-gray-650'
                                                }`}
                                                href={item.href || `#`}
                                                onClick={item.onClick}
                                            >
                                                {item.name}
                                            </a>
                                        </Tooltip>
                                    ) : (
                                        <a
                                            className={`hover:tw-text-gray-650 tw-block tw-no-underline tw-w-full tw-py-3 tw-px-4 tw-clear-both tw-font-normal tw-text-inherit tw-whitespace-nowrap tw-bg-transparent hover:tw-no-underline ${
                                                item.disabled
                                                    ? '!tw-text-gray-300 !tw-cursor-not-allowed !tw-pointer-events-none'
                                                    : 'tw-cursor-pointer hover:tw-bg-gray-200 !tw-text-gray-650'
                                            }`}
                                            href={item.href || `#`}
                                            key={key}
                                            onClick={item.onClick}
                                        >
                                            {item.name}
                                        </a>
                                    ),
                                )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
